import React, { Component } from "react";
import Helmet from "react-helmet";
import {
  FormControl,
  TextField,
} from "@material-ui/core";
import Footer from "../Footer/Footer";
import Navbar from "../Navbar/Navbar";
import LoginModal from "../LoginModal/LoginModal";
import { auth } from "../AuthService";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import constants from "../Constants/constants";
import LazyImage from "../LazyImage/LazyImage";
import "./Decks.css";

class Decks extends Component {
  constructor(props) {
    super(props);
    this.sentinelRef = React.createRef();
    this.observer = null;
    this.state = {
      search: "",
      decks: [],
      visibleDecks: 24,
      isLoggedIn: auth.loggedIn(),
      showLoginModal: false,
      copyingDeckId: null,
      copiedDeckIds: new Set(),
      pendingCopyDeckId: null,
      showBackToTop: false,
    };
  }

  componentDidMount() {
    this.fetchDecks();
    window.addEventListener("authChange", this.handleAuthChange);
    window.addEventListener("scroll", this.handleScroll);
    this.setupScrollObserver();
  }

  componentDidUpdate() {
    this.setupScrollObserver();
  }

  componentWillUnmount() {
    window.removeEventListener("authChange", this.handleAuthChange);
    window.removeEventListener("scroll", this.handleScroll);
    if (this.observer) this.observer.disconnect();
  }

  handleScroll = () => {
    this.setState({ showBackToTop: window.scrollY > 400 });
  }

  scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  setupScrollObserver() {
    if (this.observer) this.observer.disconnect();
    if (!this.sentinelRef.current) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.loadMoreDecks();
        }
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this.sentinelRef.current);
  }

  handleAuthChange = () => {
    this.setState({ isLoggedIn: auth.loggedIn() });
  }

  fetchDecks(search = "") {
    let endpoint = `${constants.DOMAIN}api/v1/guest/deck?search=${search}`;
    fetch(endpoint, {
      mode: "cors",
      method: "GET",
    })
      .then(async (res) => {
        res = await res.json();
        let decks = res.decks.sort((a, b) =>
          a.updatedAt > b.updatedAt ? -1 : a.updatedAt < b.updatedAt ? 1 : 0
        );
        this.setState({
          decks: decks,
          visibleDecks: 24,
        });
      })
      .catch((err) => {
        console.log(err);
        alert("There was an error, try again later");
      });
  }

  loadMoreDecks() {
    this.setState((prevState) => ({
      visibleDecks: prevState.visibleDecks + 24,
    }));
  }

  handleSearchChange(value) {
    this.setState({ search: value }, () => {
      this.fetchDecks(this.state.search);
    });
  }

  renderTextInput(
    id,
    label,
    value,
    width,
    inputProps,
    disabled = false,
    error = false
  ) {
    return (
      <FormControl style={{ width: width }}>
        <TextField
          error={error}
          variant="outlined"
          id={id}
          label={label}
          value={value || ""}
          disabled={disabled}
          InputProps={inputProps}
          autoComplete="nope"
          onChange={(inputValue) => {
            this.handleSearchChange(inputValue.target.value);
          }}
        />
      </FormControl>
    );
  }

  handleDeckClick(deckId) {
    this.props.navigate(`/deck/${deckId}`);
  }

  handleCopyDeck = (e, deckId) => {
    e.stopPropagation();
    if (!this.state.isLoggedIn) {
      this.setState({ pendingCopyDeckId: deckId, showLoginModal: true });
      return;
    }
    this.performCopy(deckId);
  }

  performCopy = (deckId) => {
    this.setState({ copyingDeckId: deckId });

    auth.authFetch(`${constants.DOMAIN}api/v1/decks/${deckId}/copy`, {
      method: 'POST',
    })
      .then((res) => res.json())
      .then(() => {
        this.setState((prev) => {
          const updated = new Set(prev.copiedDeckIds);
          updated.add(deckId);
          return { copyingDeckId: null, copiedDeckIds: updated };
        });
      })
      .catch(() => {
        this.setState({ copyingDeckId: null });
        alert("Failed to copy deck. Please try again.");
      });
  }

  handleLoginSuccess = (userData) => {
    window.dispatchEvent(new Event("authChange"));
    const pendingId = this.state.pendingCopyDeckId;
    this.setState({ showLoginModal: false, isLoggedIn: true, pendingCopyDeckId: null }, () => {
      if (pendingId) {
        const deck = this.state.decks.find(d => d._id === pendingId);
        const currentUserId = auth.getUser()?._id;
        if (deck?.user?._id === currentUserId) {
          return; // It's their own deck, skip copy
        }
        this.performCopy(pendingId);
      }
    });
  }

  getCopyButtonText(deckId) {
    if (this.state.copyingDeckId === deckId) return "Copying...";
    if (this.state.copiedDeckIds.has(deckId)) return "Copied!";
    if (!this.state.isLoggedIn) return "Login to Copy";
    return "Copy Deck";
  }

  render() {
    const { search, decks, visibleDecks, showLoginModal, copyingDeckId, copiedDeckIds, isLoggedIn } = this.state;
    const filteredDecks = decks.filter((d) => d.deck.length > 49);
    const currentUserId = isLoggedIn ? auth.getUser()?._id : null;

    return (
      <div>
        <Helmet>
          <title>Community Decks</title>
          <meta
            name="description"
            content="Checkout all of the decks the community has built on the Primal TCG App!"
          />
          <meta
            property="og:title"
            content="Primal TCG Community Decks"
          />
          <meta
            property="og:description"
            content="Checkout all of the decks the community has built on the Primal TCG App!"
          />
          <meta
            property="og:image"
            content="https://pbs.twimg.com/media/FxkDQAsWYAImo4-?format=jpg&name=4096x4096"
          />
        </Helmet>
        <div className="row bkg-red full-h">
          <Navbar />
          <div className="col-xs-12 col-sm-8 col-md-8 col-xl-8 offset-sm-2 offset-md-2 offset-xl-2 floating-section">
            <h1 className="mb-2vh">Filter Deck</h1>
            <div className="container mb-2vh">
              {this.renderTextInput(
                "search",
                "Search by Deck Name",
                search,
                "100%"
              )}
              <div className="row">
                {filteredDecks.slice(0, visibleDecks).map((deck) => (
                  <div
                    key={deck._id}
                    className="col-sm-2 deck-card"
                    onClick={() => this.handleDeckClick(deck._id)}
                  >
                    <LazyImage
                      className="deck-card-img"
                      src={constants.DOMAIN + deck.image}
                      alt={deck.name}
                    />
                    <p className="deck-card-name" title={deck.name}>{deck.name}</p>
                    {deck.user && deck.user.userName && (
                      <p className="deck-card-author">by {deck.user.userName}</p>
                    )}
                    {(!deck.user || deck.user._id !== currentUserId) && (
                      <button
                        className={`deck-copy-btn${copiedDeckIds.has(deck._id) ? " copied" : ""}${copyingDeckId === deck._id ? " copying" : ""}`}
                        onClick={(e) => this.handleCopyDeck(e, deck._id)}
                        disabled={copyingDeckId === deck._id || copiedDeckIds.has(deck._id)}
                      >
                        {this.getCopyButtonText(deck._id)}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {visibleDecks < filteredDecks.length && (
                <div ref={this.sentinelRef} style={{ height: '1px' }} />
              )}
            </div>
          </div>
          <Footer />
        </div>
        {this.state.showBackToTop && (
          <button
            className="back-to-top-btn"
            onClick={this.scrollToTop}
            title="Back to top"
            aria-label="Scroll back to top of page"
          >
            <KeyboardArrowUpIcon aria-hidden="true" />
          </button>
        )}
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => this.setState({ showLoginModal: false, pendingCopyDeckId: null })}
          onLoginSuccess={this.handleLoginSuccess}
          darkMode={false}
        />
      </div>
    );
  }
}

export default Decks;
